import React, { useState, useEffect, useRef, useContext } from "react";
import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import { makeStyles } from "@material-ui/core/styles";
import { green } from "@material-ui/core/colors";

import {
  Dialog,
  DialogContent,
  DialogTitle,
  Button,
  DialogActions,
  CircularProgress,
  TextField,
  Switch,
  FormControlLabel,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Box,
  Typography,
} from "@material-ui/core";
import { AttachFile, DeleteOutline } from "@material-ui/icons";
import { head } from "lodash";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import QueueSelect from "../QueueSelect";
import HolidayPeriodManager from "../HolidayPeriodManager";
import { SocketContext } from "../../context/Socket/SocketContext";

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    flexWrap: "wrap",
  },

  multFieldLine: {
    display: "flex",
    "& > *:not(:last-child)": {
      marginRight: theme.spacing(1),
    },
  },

  btnWrapper: {
    position: "relative",
  },

  buttonProgress: {
    color: green[500],
    position: "absolute",
    top: "50%",
    left: "50%",
    marginTop: -12,
    marginLeft: -12,
  },
}));

const SessionSchema = Yup.object().shape({
  name: Yup.string()
    .min(2, "Too Short!")
    .max(50, "Too Long!")
    .required("Required"),
});

const WhatsAppModal = ({ open, onClose, whatsAppId }) => {
  const classes = useStyles();
  const socketManager = useContext(SocketContext);
  const initialState = {
    name: "",
    greetingMessage: "",
    complationMessage: "",
    outOfHoursMessage: "",
    ratingMessage: "",
    isDefault: false,
    token: "",
    provider: "beta",
    //timeSendQueue: 0,
    //sendIdQueue: 0,
    expiresInactiveMessage: "",
    expiresTicket: 0,
    timeUseBotQueues: 0,
    maxUseBotQueues: 3,
    pix: "",
    pixMessage: "",
    chatbotInactivityTimeout: 0,
    chatbotInactivityMessage: ""
  };
  const [whatsApp, setWhatsApp] = useState(initialState);
  const [selectedQueueIds, setSelectedQueueIds] = useState([]);
  const [queues, setQueues] = useState([]);
  const [selectedQueueId, setSelectedQueueId] = useState(null)
  const [selectedPrompt, setSelectedPrompt] = useState(null);
  const [prompts, setPrompts] = useState([]);
  const [greetingAttachment, setGreetingAttachment] = useState(null);
  const attachmentFile = useRef(null);
  const [holidayPeriodEnabled, setHolidayPeriodEnabled] = useState("disabled");
  
    useEffect(() => {
    const fetchSession = async () => {
      if (!whatsAppId) return;

      try {
        const { data } = await api.get(`whatsapp/${whatsAppId}?session=0`);
        setWhatsApp(data);

        const whatsQueueIds = data.queues?.map((queue) => queue.id);
        setSelectedQueueIds(whatsQueueIds);
		setSelectedQueueId(data.transferQueueId);
      } catch (err) {
        toastError(err);
      }
    };
    fetchSession();
  }, [whatsAppId]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/prompt");
        setPrompts(data.prompts);
      } catch (err) {
        toastError(err);
      }
    })();
  }, [whatsAppId]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/queue");
        setQueues(data);
      } catch (err) {
        toastError(err);
      }
    })();
  }, []);

  useEffect(() => {
    const fetchHolidayPeriodEnabled = async () => {
      try {
        const { data } = await api.get("/settings");
        const holidayPeriodEnabledSetting = data.find((s) => s.key === "holidayPeriodEnabled");
        if (holidayPeriodEnabledSetting) {
          setHolidayPeriodEnabled(holidayPeriodEnabledSetting.value);
        }
      } catch (err) {
        toastError(err);
      }
    };
    
    fetchHolidayPeriodEnabled();
    
    // Listener para atualizar quando a configuração mudar via socket
    const companyId = localStorage.getItem("companyId");
    if (companyId && socketManager) {
      const socket = socketManager.getSocket(companyId);
      
      socket.on(`company-${companyId}-settings`, (data) => {
        if (data.action === "update" && data.setting.key === "holidayPeriodEnabled") {
          setHolidayPeriodEnabled(data.setting.value);
        }
      });
      
      return () => {
        socket.off(`company-${companyId}-settings`);
      };
    }
  }, [socketManager, open]);

  const handleAttachmentFile = (e) => {
    const file = head(e.target.files);
    if (file) {
      setGreetingAttachment(file);
    }
  };

  const deleteGreetingMedia = async () => {
    if (greetingAttachment) {
      setGreetingAttachment(null);
      attachmentFile.current.value = null;
    }

    if (whatsApp.greetingMediaPath && whatsAppId) {
      try {
        await api.delete(`/whatsapp/${whatsAppId}/greeting-media`);
        setWhatsApp((prev) => ({
          ...prev,
          greetingMediaPath: null,
          greetingMediaName: null,
        }));
        toast.success("Mídia removida com sucesso");
      } catch (err) {
        toastError(err);
      }
    }
  };

  const handleSaveWhatsApp = async (values) => {
    const whatsappData = {
      ...values, 
      queueIds: selectedQueueIds, 
      transferQueueId: selectedQueueId,
      promptId: selectedPrompt ? selectedPrompt : null,
    };
    delete whatsappData["queues"];
    delete whatsappData["session"];
    
    // DEBUG: Log dos campos de timeout
    console.log("DEBUG - Campos de timeout:", {
      chatbotInactivityTimeout: whatsappData.chatbotInactivityTimeout,
      chatbotInactivityMessage: whatsappData.chatbotInactivityMessage
    });
    console.log("DEBUG - Todos os dados:", whatsappData);

    try {
      if (whatsAppId) {
        await api.put(`/whatsapp/${whatsAppId}`, whatsappData);
        
        // Upload de mídia se houver
        if (greetingAttachment) {
          const formData = new FormData();
          formData.append("file", greetingAttachment);
          await api.post(`/whatsapp/${whatsAppId}/greeting-media`, formData);
        }
      } else {
        const { data } = await api.post("/whatsapp", whatsappData);
        
        // Upload de mídia se houver
        if (greetingAttachment) {
          const formData = new FormData();
          formData.append("file", greetingAttachment);
          await api.post(`/whatsapp/${data.id}/greeting-media`, formData);
        }
      }
      toast.success(i18n.t("whatsappModal.success"));
      handleClose();
    } catch (err) {
      toastError(err);
    }
  };

  const handleChangeQueue = (e) => {
    setSelectedQueueIds(e);
    setSelectedPrompt(null);
  };

  const handleChangePrompt = (e) => {
    setSelectedPrompt(e.target.value);
    setSelectedQueueIds([]);
  };

  const handleClose = () => {
    onClose();
    setWhatsApp(initialState);
	  setSelectedQueueId(null);
    setSelectedQueueIds([]);
    setGreetingAttachment(null);
  };

  return (
    <div className={classes.root}>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        scroll="paper"
      >
        <DialogTitle>
          {whatsAppId
            ? i18n.t("whatsappModal.title.edit")
            : i18n.t("whatsappModal.title.add")}
        </DialogTitle>
        <Formik
          initialValues={whatsApp}
          enableReinitialize={true}
          validationSchema={null}
          onSubmit={(values, actions) => {
            setTimeout(() => {
              handleSaveWhatsApp(values);
              actions.setSubmitting(false);
            }, 400);
          }}
        >
          {({ values, touched, errors, isSubmitting }) => (
            <Form>
              <DialogContent dividers>
                <div className={classes.multFieldLine}>
                  <Grid spacing={2} container>
                    <Grid item>
                      <Field
                        as={TextField}
                        label={i18n.t("whatsappModal.form.name")}
                        autoFocus
                        name="name"
                        error={touched.name && Boolean(errors.name)}
                        helperText={touched.name && errors.name}
                        variant="outlined"
                        margin="dense"
                        className={classes.textField}
                      />
                    </Grid>
                    <Grid style={{ paddingTop: 15 }} item>
                      <FormControlLabel
                        control={
                          <Field
                            as={Switch}
                            color="primary"
                            name="isDefault"
                            checked={values.isDefault}
                          />
                        }
                        label={i18n.t("whatsappModal.form.default")}
                      />
                    </Grid>
                  </Grid>
                </div>
                <div>
                  <Field
                    as={TextField}
                    label={i18n.t("queueModal.form.greetingMessage")}
                    type="greetingMessage"
                    multiline
                    rows={4}
                    fullWidth
                    name="greetingMessage"
                    error={
                      touched.greetingMessage && Boolean(errors.greetingMessage)
                    }
                    helperText={
                      touched.greetingMessage && errors.greetingMessage
                    }
                    variant="outlined"
                    margin="dense"
                  />
                </div>
                {/* Mídia de Saudação */}
                <div>
                  <Typography variant="subtitle2" style={{ marginBottom: 8, marginTop: 16 }}>
                    Mídia de Saudação (Opcional)
                  </Typography>
                  <div style={{ display: "none" }}>
                    <input
                      type="file"
                      ref={attachmentFile}
                      onChange={(e) => handleAttachmentFile(e)}
                      accept="image/*"
                    />
                  </div>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={6}>
                      <Button
                        variant="outlined"
                        startIcon={<AttachFile />}
                        onClick={() => attachmentFile.current?.click()}
                        fullWidth
                      >
                        {greetingAttachment ? greetingAttachment.name : (whatsApp.greetingMediaName || "Anexar Imagem")}
                      </Button>
                    </Grid>
                    {(greetingAttachment || whatsApp.greetingMediaPath) && (
                      <Grid item xs={12}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography variant="body2" color="textSecondary">
                            {greetingAttachment ? greetingAttachment.name : whatsApp.greetingMediaName}
                          </Typography>
                          <IconButton
                            size="small"
                            onClick={deleteGreetingMedia}
                            color="secondary"
                          >
                            <DeleteOutline />
                          </IconButton>
                        </Box>
                      </Grid>
                    )}
                  </Grid>
                </div>
                <div>
                  <Field
                    as={TextField}
                    label={i18n.t("queueModal.form.complationMessage")}
                    type="complationMessage"
                    multiline
                    rows={4}
                    fullWidth
                    name="complationMessage"
                    error={
                      touched.complationMessage &&
                      Boolean(errors.complationMessage)
                    }
                    helperText={
                      touched.complationMessage && errors.complationMessage
                    }
                    variant="outlined"
                    margin="dense"
                  />
                </div>
                <div>
                  <Field
                    as={TextField}
                    label="Mensagem de Encerramento por Não Escolher Fila"
                    type="text"
                    multiline
                    rows={4}
                    fullWidth
                    name="chatbotInactivityMessage"
                    placeholder="Digite a mensagem que será enviada ao encerrar por inatividade"
                    variant="outlined"
                    margin="dense"
                  />
                </div>
                <div>
                  <Field
                    as={TextField}
                    label="Tempo sem Escolher Fila (minutos)"
                    type="number"
                    fullWidth
                    name="chatbotInactivityTimeout"
                    placeholder="Digite o tempo em minutos (0 para desabilitar)"
                    variant="outlined"
                    margin="dense"
                    inputProps={{ min: 0 }}
                  />
                </div>
                <div>
                  <Field
                    as={TextField}
                    label={i18n.t("queueModal.form.outOfHoursMessage")}
                    type="outOfHoursMessage"
                    multiline
                    rows={4}
                    fullWidth
                    name="outOfHoursMessage"
                    error={
                      touched.outOfHoursMessage &&
                      Boolean(errors.outOfHoursMessage)
                    }
                    helperText={
                      touched.outOfHoursMessage && errors.outOfHoursMessage
                    }
                    variant="outlined"
                    margin="dense"
                  />
                </div>
                <div>
                  <Field
                    as={TextField}
                    label={i18n.t("whatsappModal.form.expiresInactiveMessage")}
                    multiline
                    rows={4}
                    fullWidth
                    name="expiresInactiveMessage"
                    error={touched.expiresInactiveMessage && Boolean(errors.expiresInactiveMessage)}
                    helperText={touched.expiresInactiveMessage && errors.expiresInactiveMessage}
                    variant="outlined"
                    margin="dense"
                  />
                </div>
                <div>
                  <Field
                    as={TextField}
                    label={i18n.t("whatsappModal.form.expiresTicket")}
                    type="number"
                    fullWidth
                    name="expiresTicket"
                    placeholder="Digite o tempo em minutos (0 para desabilitar)"
                    variant="outlined"
                    margin="dense"
                    inputProps={{ min: 0 }}
                  />
                </div>
                <div>
                  <Field
                    as={TextField}
                    label={i18n.t("queueModal.form.ratingMessage")}
                    type="ratingMessage"
                    multiline
                    rows={4}
                    fullWidth
                    name="ratingMessage"
                    error={
                      touched.ratingMessage && Boolean(errors.ratingMessage)
                    }
                    helperText={touched.ratingMessage && errors.ratingMessage}
                    variant="outlined"
                    margin="dense"
                  />
                </div>
                <div>
                  <Field
                    as={TextField}
                    label={i18n.t("queueModal.form.token")}
                    type="token"
                    fullWidth
                    name="token"
                    variant="outlined"
                    margin="dense"
                  />
                </div>
                <div>
                  <Field
                    as={TextField}
                    label="Chave PIX"
                    type="text"
                    fullWidth
                    name="pix"
                    placeholder="Digite a chave PIX (Email, CPF, CNPJ, Telefone ou Chave Aleatória)"
                    variant="outlined"
                    margin="dense"
                  />
                </div>
                <div>
                  <Field
                    as={TextField}
                    label="Mensagem Personalizada PIX"
                    type="text"
                    multiline
                    rows={4}
                    fullWidth
                    name="pixMessage"
                    placeholder="Digite a mensagem que será enviada ANTES da chave PIX"
                    variant="outlined"
                    margin="dense"
                  />
                </div>
                <QueueSelect
                  selectedQueueIds={selectedQueueIds}
                  onChange={(selectedIds) => handleChangeQueue(selectedIds)}
                />
                <FormControl
                  margin="dense"
                  variant="outlined"
                  fullWidth
                >
                  <InputLabel>
                    {i18n.t("whatsappModal.form.prompt")}
                  </InputLabel>
                  <Select
                    labelId="dialog-select-prompt-label"
                    id="dialog-select-prompt"
                    name="promptId"
                    value={selectedPrompt || ""}
                    onChange={handleChangePrompt}
                    label={i18n.t("whatsappModal.form.prompt")}
                    fullWidth
                    MenuProps={{
                      anchorOrigin: {
                        vertical: "bottom",
                        horizontal: "left",
                      },
                      transformOrigin: {
                        vertical: "top",
                        horizontal: "left",
                      },
                      getContentAnchorEl: null,
                    }}
                  >
                    {prompts.map((prompt) => (
                      <MenuItem
                        key={prompt.id}
                        value={prompt.id}
                      >
                        {prompt.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <div>
                  <h3>{i18n.t("whatsappModal.form.queueRedirection")}</h3>
                  <p>{i18n.t("whatsappModal.form.queueRedirectionDesc")}</p>
				<Grid container spacing={2}>
                  <Grid item sm={6} >
                    <Field
                      fullWidth
                      type="number"
                      as={TextField}
                      label='Transferir após x (minutos)'
                      name="timeToTransfer"
                      error={touched.timeToTransfer && Boolean(errors.timeToTransfer)}
                      helperText={touched.timeToTransfer && errors.timeToTransfer}
                      variant="outlined"
                      margin="dense"
                      className={classes.textField}
                      InputLabelProps={{ shrink: values.timeToTransfer ? true : false }}
                    />

                  </Grid>

                  <Grid item sm={6}>
                    <QueueSelect
                      selectedQueueIds={selectedQueueId}
                      onChange={(selectedId) => {
                        setSelectedQueueId(selectedId)
                      }}
                      multiple={false}
                      title={'Fila de Transferência'}
                    />
                  </Grid>

                  </Grid>
                </div>
                {/* Recesso/Feriados */}
                {whatsAppId && holidayPeriodEnabled === "enabled" && (
                  <div style={{ marginTop: 16 }}>
                    <HolidayPeriodManager
                      whatsappId={whatsAppId}
                      companyId={localStorage.getItem("companyId")}
                    />
                  </div>
                )}
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={handleClose}
                  color="secondary"
                  disabled={isSubmitting}
                  variant="outlined"
                >
                  {i18n.t("whatsappModal.buttons.cancel")}
                </Button>
                <Button
                  type="submit"
                  color="primary"
                  disabled={isSubmitting}
                  variant="contained"
                  className={classes.btnWrapper}
                >
                  {whatsAppId
                    ? i18n.t("whatsappModal.buttons.okEdit")
                    : i18n.t("whatsappModal.buttons.okAdd")}
                  {isSubmitting && (
                    <CircularProgress
                      size={24}
                      className={classes.buttonProgress}
                    />
                  )}
                </Button>
              </DialogActions>
            </Form>
          )}
        </Formik>
      </Dialog>
    </div>
  );
};

export default React.memo(WhatsAppModal);
